import { formatTime } from '../utils/formatDate';
import { z, PlanningTaskSchema } from '@guestguru/shared';
import { apiGet, apiPost, apiPut, apiDelete , planningPath } from './PlanningApiClient';
import { getTeamMembersCache } from './teamMembersCache';

// ── In-memory cache (no localStorage for app data) ──
let tasksCache: Task[] = [];
let globalAuditLogCallback: ((action: string, details: Record<string, unknown>) => void) | null = null;

// Register callback for global audit log (called from App.tsx)
export function registerGlobalAuditLogCallback(callback: (action: string, details: Record<string, unknown>) => void) {
  globalAuditLogCallback = callback;
}

export type Priority = 'P0' | 'P1' | 'P2' | 'P3';
export type TaskStatus = 'Not Started' | 'In Progress' | 'Done';
// A task's own workflow status (above) and whether it's currently blocked
// are independent facets, not one 4-value enum — a task can be "In Progress"
// AND blocked at the same time. 'internal' = blocked by something within our
// own team/process; 'external' = waiting on a vendor/third party. `null`
// means not blocked (or, for a migrated legacy row, "blocked but not yet
// triaged" — see deriveStatusAndBlockedOn below).
export type BlockedOn = 'internal' | 'external' | null;

export interface ActivityLogEntry {
  id: string;
  action: string;
  timestamp: string;
  user: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
}

export type TaskType = 'task' | 'run_of_show';

export interface Task {
  id: string;
  task: string;
  priority: Priority;
  ownerTeam: string;
  assignees: string[];
  status: TaskStatus;
  blockedOn?: BlockedOn;
  dueDateISO?: string; // ISO "2026-06-15" — use fmtDueDate() for display
  startDateISO?: string;
  startTime?: string;
  endTime?: string;
  milestone?: string;
  deliverable?: string;
  notes?: string;
  eventType?: string;
  eventIds?: string[];
  vendorIds?: string[];
  teamTag?: string;
  phase?: 'foundation' | 'design' | 'lock-in' | 'make-order' | 'finalise' | 'event-week' | 'confirmation' | 'research';
  taskType?: TaskType;
  isCustom: boolean;
  archived?: boolean;
  activity_log?: ActivityLogEntry[];
  parentTaskId?: string;
  dependsOnTaskIds?: string[];
}

/** Task ids are uuids in the kal shape; only a purely numeric (legacy) id is sent as a number. */
export function toApiTaskId(id: string | number): string | number {
  const s = String(id);
  return /^\d+$/.test(s) ? Number(s) : s;
}

export function loadTasks(): Task[] {
  return tasksCache;
}

/** Format an ISO date string as British DD/MM/YYYY for display */
export function fmtDueDate(iso: string | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

export function saveTasks(tasks: Task[]): void {
  tasksCache = tasks;
}

/**
 * Create a new task by POSTing to the API first.
 * The API assigns the integer ID — we use that so subsequent
 * PUT/DELETE calls go to the correct path (e.g. /tasks/247).
 */
export async function createTask(data: Omit<Task, 'id' | 'isCustom'>): Promise<Task> {
  const payload = { ...data, isCustom: true };

  // POST to API — must await to get the DB-assigned integer ID; send snake_case to match backend schema
  const created = await apiPost<Record<string, unknown>>(planningPath('/tasks'), {
    task: payload.task,
    priority: payload.priority,
    status: payload.status,
    owner_team: payload.ownerTeam ?? null,
    assignees: payload.assignees ?? [],
    due_date: payload.dueDateISO ?? null,
    start_date: payload.startDateISO ?? null,
    start_time: payload.startTime ?? null,
    end_time: payload.endTime ?? null,
    milestone: payload.milestone ?? null,
    deliverable: payload.deliverable ?? null,
    notes: payload.notes ?? null,
    phase: payload.phase ?? null,
    is_custom: true,
    archived: false,
    event_ids: payload.eventIds ?? [],
    task_type: payload.taskType ?? 'task',
    parent_task_id: payload.parentTaskId ? toApiTaskId(payload.parentTaskId) : null,
    depends_on_task_ids: (payload.dependsOnTaskIds ?? []).map(toApiTaskId),
  });

  const newTask: Task = {
    ...payload,
    id: String((created as { id?: number | string }).id ?? ''),
  };

  tasksCache = [...tasksCache, newTask];
  return newTask;
}

/** Track field changes and add activity log entry */
function trackChanges(old: Task, updated: Task): ActivityLogEntry[] {
  const entries: ActivityLogEntry[] = [];
  const trackedFields = ['status', 'blockedOn', 'priority', 'assignees', 'dueDateISO', 'notes'] as const;

  trackedFields.forEach(field => {
    const oldVal = old[field];
    const newVal = updated[field];

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      const oldStr = Array.isArray(oldVal) ? oldVal.join(', ') : String(oldVal || '');
      const newStr = Array.isArray(newVal) ? newVal.join(', ') : String(newVal || '');

      entries.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        action: `${field} changed`,
        timestamp: formatTime(new Date()),
        user: 'You',
        field,
        oldValue: oldStr,
        newValue: newStr,
      });
    }
  });

  return entries;
}

const ACTIVE_STATUSES: TaskStatus[] = ['In Progress', 'Done'];

/** Thrown by updateTask when the move to In Progress/Done is blocked by an unfinished dependency. */
export class TaskDependencyBlockedError extends Error {
  constructor(public readonly blockingTaskIds: string[]) {
    super(`Blocked by unfinished dependenc${blockingTaskIds.length === 1 ? 'y' : 'ies'}: ${blockingTaskIds.map(id => `TA-${id.padStart(3, '0')}`).join(', ')}`);
    this.name = 'TaskDependencyBlockedError';
  }
}

/** Returns the IDs of `task`'s dependencies (from the in-memory cache) that aren't Done yet. */
export function getBlockingTaskIds(task: Task): string[] {
  if (!task.dependsOnTaskIds?.length) return [];
  return task.dependsOnTaskIds.filter(depId => {
    const dep = tasksCache.find(t => t.id === depId);
    // An archived dependency is no longer a real, live blocker — archiving it
    // effectively removes it from consideration, same as if it were deleted.
    if (!dep || dep.archived) return false;
    return dep.status !== 'Done';
  });
}

// The 30s background poll (usePlanningSync) does a full GET /tasks and used
// to blindly overwrite the whole cache. If that GET was already in flight
// when a save landed, its stale response arrived after the optimistic write
// and clobbered it back to the pre-save value — a save that appeared to
// silently "revert" seconds later, for any field, for any user. Tracking a
// short-lived write timestamp per task id lets refreshTasksFromApi() skip
// re-applying a fetched row that's older than the local edit it would erase.
const RECENT_WRITE_WINDOW_MS = 20_000;
const recentLocalWrites = new Map<string, number>();

/**
 * Update an existing task.
 * Optimistically updates the cache first, then persists to the API.
 */
export async function updateTask(id: string, updates: Partial<Task>): Promise<Task | null> {
  const idx = tasksCache.findIndex(t => t.id === id);
  if (idx === -1) return null;
  const old = tasksCache[idx];
  const updated = { ...old, ...updates };
  recentLocalWrites.set(id, Date.now());

  if (updates.status && ACTIVE_STATUSES.includes(updates.status)) {
    const blocking = getBlockingTaskIds(old);
    if (blocking.length > 0) throw new TaskDependencyBlockedError(blocking);
  }

  // Track changes for activity log
  const changes = trackChanges(old, updated);
  const existingLog = old.activity_log || [];
  updated.activity_log = [...existingLog, ...changes];

  // Emit to global audit log
  changes.forEach(change => {
    if (globalAuditLogCallback) {
      globalAuditLogCallback(`Task: ${change.action}`, {
        task: updated.task,
        field: change.field,
        oldValue: change.oldValue,
        newValue: change.newValue,
      });
    }
  });

  // Optimistic cache update (synchronous — callers that refresh immediately see this)
  const newCache = [...tasksCache];
  newCache[idx] = updated;
  tasksCache = newCache;

  // Persist to API — send snake_case to match backend schema
  try {
    await apiPut(planningPath(`/tasks/${id}`), {
      task: updated.task,
      priority: updated.priority,
      status: updated.status,
      // Not yet in the shared PlanningTaskSchema (backend has no blocked_on
      // column as of 2026-09-19) — sent anyway so it round-trips once the
      // gg-shared/ans-backend schema picks it up; today the backend/shared
      // schema silently strips unknown keys, so this facet only persists
      // within this session's in-memory cache until that lands.
      blocked_on: updated.blockedOn ?? null,
      owner_team: updated.ownerTeam ?? null,
      assignees: updated.assignees ?? [],
      due_date: updated.dueDateISO ?? null,
      start_date: updated.startDateISO ?? null,
      start_time: updated.startTime ?? null,
      end_time: updated.endTime ?? null,
      milestone: updated.milestone ?? null,
      deliverable: updated.deliverable ?? null,
      notes: updated.notes ?? null,
      phase: updated.phase ?? null,
      is_custom: updated.isCustom,
      archived: updated.archived ?? false,
      event_ids: updated.eventIds ?? [],
      parent_task_id: updated.parentTaskId ? toApiTaskId(updated.parentTaskId) : null,
      depends_on_task_ids: (updated.dependsOnTaskIds ?? []).map(toApiTaskId),
    });
  } catch (err) {
    // Server rejected the update (e.g. dependency-blocked race) — revert the optimistic cache write.
    const revertCache = [...tasksCache];
    revertCache[idx] = old;
    tasksCache = revertCache;
    recentLocalWrites.delete(id);
    throw err;
  }

  return updated;
}

/** Soft-delete: mark task as archived */
export async function archiveTask(id: string): Promise<Task | null> {
  return updateTask(id, { archived: true });
}

/** Restore an archived task */
export async function restoreTask(id: string): Promise<Task | null> {
  return updateTask(id, { archived: false });
}

/** Permanent delete — only allowed for already-archived tasks */
export async function deleteTask(id: string): Promise<boolean> {
  const task = tasksCache.find(t => t.id === id);
  if (!task || !task.archived) return false;

  tasksCache = tasksCache.filter(t => t.id !== id);
  await apiDelete(planningPath(`/tasks/${id}`));
  return true;
}

// ── API sync helpers ──

/**
 * Pull tasks from the Planning API into the in-memory cache.
 * Call on app/component mount to hydrate cache with latest API data.
 */
type ApiTask = z.infer<typeof PlanningTaskSchema> & { task_type?: string | null; blocked_on?: string | null };

// Live data has accumulated status values outside the canonical TaskStatus
// union — lowercase 'blocked', snake_case 'in_progress', and a 'To Do' that
// predates the current label set (307/65/29/24/14/6/2 rows respectively,
// confirmed live). Cast-without-normalizing meant every one of those tasks
// counted toward totals but matched none of the exact-string status filters
// used throughout the app (Dashboard's task breakdown, etc) — silently
// undercounting by however many legacy-status tasks existed.
//
// 'blocked' is deliberately NOT in this map (2026-09-19): Blocked used to be
// a 4th TaskStatus value, but it's now a separate `blockedOn` facet — see
// deriveStatusAndBlockedOn below for how the 307 legacy 'blocked' rows get
// split into a real status plus a blockedOn value.
const STATUS_ALIASES: Record<string, TaskStatus> = {
  'not started': 'Not Started',
  'to do': 'Not Started',
  'todo': 'Not Started',
  'in progress': 'In Progress',
  'in_progress': 'In Progress',
  'done': 'Done',
  'completed': 'Done',
};

function normalizeTaskStatus(status: string | null | undefined): TaskStatus {
  if (!status) return 'Not Started';
  return STATUS_ALIASES[status.trim().toLowerCase()] ?? (status as TaskStatus);
}

/**
 * Splits the legacy free-text `status` column into the new (status,
 * blockedOn) facet pair.
 *
 * Migration rule for a row still carrying the legacy 'blocked' status
 * (case-insensitive; 307 live rows confirmed 2026-09-19):
 *   - base status is inferred from whether work had visibly started:
 *     `start_date` set → 'In Progress' (someone had begun it before it got
 *     blocked); otherwise → 'Not Started'.
 *   - blockedOn is left `null` rather than guessed — the legacy column has
 *     no signal distinguishing an internal vs external blocker, and
 *     defaulting to one bucket would misclassify roughly half of them. A
 *     `null` blockedOn on a row whose raw status was 'blocked' is
 *     surfaced in the UI as "needs review" so a human can pick internal vs
 *     external per-task instead.
 *
 * This runs at READ time only and never writes anything back automatically —
 * a task keeps its legacy 'blocked' status server-side until a user actually
 * edits it (status or blockedOn), at which point updateTask() persists the
 * new shape. That's intentional: this repo has no access to run a real
 * migration against the production Planning DB (owned by ans-backend, a
 * separate repo/deploy), so the safe default is "reinterpret on the way in,
 * only write on explicit user action."
 */
export function deriveStatusAndBlockedOn(row: {
  status: string | null;
  blocked_on?: string | null;
  start_date: string | null;
}): { status: TaskStatus; blockedOn: BlockedOn } {
  // Forward-compatible: once the backend actually carries a blocked_on
  // column, prefer it outright over re-deriving from the legacy status text.
  if (row.blocked_on === 'internal' || row.blocked_on === 'external') {
    return { status: normalizeTaskStatus(row.status), blockedOn: row.blocked_on };
  }
  const raw = (row.status ?? '').trim().toLowerCase();
  if (raw === 'blocked') {
    return { status: row.start_date ? 'In Progress' : 'Not Started', blockedOn: null };
  }
  return { status: normalizeTaskStatus(row.status), blockedOn: null };
}

function mapApiTask(row: ApiTask): Task {
  const validPhases = ['foundation', 'design', 'lock-in', 'make-order', 'finalise', 'event-week', 'confirmation', 'research'] as const;
  type PhaseType = typeof validPhases[number];
  const phase = (validPhases as readonly string[]).includes(row.phase ?? '')
    ? (row.phase as PhaseType)
    : undefined;
  const { status, blockedOn } = deriveStatusAndBlockedOn(row);
  return {
    id: String(row.id),
    task: row.task,
    priority: (row.priority ?? 'P3') as Priority,
    ownerTeam: row.owner_team ?? '',
    assignees: (row.assignees ?? []) as string[],
    status,
    blockedOn,
    dueDateISO: row.due_date ?? undefined,
    startDateISO: row.start_date ?? undefined,
    startTime: row.start_time ?? undefined,
    endTime: row.end_time ?? undefined,
    milestone: row.milestone ?? undefined,
    deliverable: row.deliverable ?? undefined,
    notes: row.notes ?? undefined,
    phase,
    isCustom: row.is_custom,
    archived: row.archived,
    eventIds: row.event_ids?.length ? row.event_ids : undefined,
    taskType: (row.task_type ?? 'task') as TaskType,
    parentTaskId: row.parent_task_id != null ? String(row.parent_task_id) : undefined,
    dependsOnTaskIds: row.depends_on_task_ids?.length ? row.depends_on_task_ids.map(String) : undefined,
  };
}

// Task comments now go through the generic CommentsService
// (entityType: 'task') — see src/services/CommentsService.ts.

/** `strict`: rethrow an API failure instead of quietly returning the cache,
 *  for screens that must say "couldn't load" rather than show stale/empty data. */
export async function refreshTasksFromApi(opts: { strict?: boolean } = {}): Promise<Task[]> {
  try {
    // GET /tasks filters strictly on `archived = $2` server-side (never
    // "both") — defaulting to archived=false. Fetching only the default
    // meant the cache never held an archived task at all, so the Tasks
    // page's Archived/All status filter had nothing to show regardless of
    // its own selection, and a direct /task/TA-xxx link to an archived task
    // 404'd. Fetch both slices and merge.
    const [activeRes, archivedRes] = await Promise.all([
      apiGet<{ tasks: ApiTask[] }>(planningPath('/tasks')),
      apiGet<{ tasks: ApiTask[] }>(planningPath('/tasks?archived=true')),
    ]);
    const rows = [...(activeRes?.tasks ?? []), ...(archivedRes?.tasks ?? [])];
    if (rows.length > 0) {
      const mapped = rows.map(mapApiTask);
      const now = Date.now();
      const currentCache = tasksCache;
      const merged = mapped.map(fresh => {
        const writtenAt = recentLocalWrites.get(fresh.id);
        if (writtenAt === undefined) return fresh;
        if (now - writtenAt >= RECENT_WRITE_WINDOW_MS) {
          recentLocalWrites.delete(fresh.id);
          return fresh;
        }
        // A local write for this task is still within its window — keep the
        // local version rather than the fetched row, which may predate it.
        return currentCache.find(t => t.id === fresh.id) ?? fresh;
      });
      saveTasks(merged);
      return merged;
    }
  } catch (err) {
    if (opts.strict) throw err;
    // API unreachable — keep existing in-memory cache
  }
  return loadTasks();
}

/**
 * Push all in-memory tasks to the API (one POST per task).
 * Useful for initial migration when the API is empty.
 */
export async function syncTasksToApi(): Promise<void> {
  const tasks = loadTasks();
  await Promise.allSettled(
    tasks.map(task => apiPost(planningPath('/tasks'), task))
  );
}

export const TEAM_OPTIONS = [
  'Aunties', 'Branding', 'Couple', 'Decorator', 'Digital',
  'Entertainment', 'Extra Curricular', 'Food + Drinks', 'GroomsParty',
  'Initators', 'Internal Logistics', 'Inventory', 'Media',
  'Planner', 'Security',
];

export const ASSIGNEE_OPTIONS = [
  'Oceania Alfa', 'Nefy', 'Nefy BB', 'Maria', 'Naphtali', 'Ibro',
  'Eko', 'Seun', 'Razeb', 'Otis', 'Kenis', 'Kenice', 'Michelle',
  'Isaac', 'Daddy', 'Aunty Theresa', 'Aunty Ronke', 'Aunty Joke', 'Aunty Eki',
  'Aunty Regina', 'Aunty Maggie', 'Mummy Bisi', 'Mummy Grace', 'Planner',
];

/** Email → display name for assignees stored as email addresses in the DB */
export const ASSIGNEE_EMAIL_MAP: Record<string, string> = {
  'oceaniaalfa@gmail.com': 'Oceania Alfa',
  'napowoicho@gmail.com': 'Naphtali',
};

/** Resolves a single assignee string: if it's a known email, returns the display name; otherwise passes through */
export function resolveAssigneeName(assignee: string): string {
  if (!assignee) return assignee;
  const lower = assignee.toLowerCase();
  // Static map first
  if (ASSIGNEE_EMAIL_MAP[lower]) return ASSIGNEE_EMAIL_MAP[lower];
  // Dynamic lookup via team members cache (handles any email stored in DB)
  if (assignee.includes('@')) {
    const cached = getTeamMembersCache();
    const match = cached.find(m => m.email?.toLowerCase() === lower && m.name);
    if (match) return match.name;
  }
  return assignee;
}

/** Resolves an array of assignees and joins them for display */
export function resolveAssigneeNames(assignees: string[]): string {
  if (!assignees?.length) return '';
  return assignees.map(resolveAssigneeName).join(', ');
}

/**
 * Returns active team member names from the in-memory cache, falling back to
 * ASSIGNEE_OPTIONS. When `team` is given, narrows to members tagged with that
 * team (TeamMemberFormPage's own `teams` field) — so reassigning a task within
 * the same team is a real filter, not just a suggestion. Falls back to the
 * unfiltered list if the filter would leave nobody to pick (e.g. no member
 * has been tagged with that team yet), rather than presenting an empty dropdown.
 */
export function loadAssigneeOptions(team?: string): string[] {
  const members = getTeamMembersCache();
  const active = members.filter(m => m.isActive !== false && m.name);
  const scoped = team ? active.filter(m => m.teams?.includes(team)) : active;
  const names = (scoped.length > 0 ? scoped : active).map(m => m.name);
  return names.length > 0 ? names : ASSIGNEE_OPTIONS;
}
